import naja from 'naja';

/**
 * Turnstile for the forms guarded by SpamGuard, shared by portal and web like antispam.latte.
 *
 * Two things must not change without knowing why: the API script is injected from here, because
 * Naja re-injects a <script> living in a snippet on every update; and nothing reaches Cloudflare
 * until the visitor touches the form, because every page carries contact modals most visitors
 * never use.
 */

const API_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const widgets = new Map();

let apiPromise = null;

const loadApi = () => {
  if (apiPromise === null) {
    apiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = API_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  return apiPromise;
};

const language = () => {
  const locale = window.ehubLocale || document.documentElement.lang;

  return locale ? locale.slice(0, 2) : 'auto';
};

const dropDetachedWidgets = () => {
  widgets.forEach((id, el) => {
    if (el.isConnected) {
      return;
    }

    // A pending entry has no widget id yet, and remove() without one grabs the last live widget.
    if (id !== null) {
      window.turnstile.remove(id);
    }

    widgets.delete(el);
  });
};

const activate = (el) => {
  if (widgets.has(el) || !el.isConnected) {
    return;
  }

  widgets.set(el, null);

  loadApi().then(() => {
    dropDetachedWidgets();

    if (!el.isConnected) {
      widgets.delete(el);

      return;
    }

    const id = window.turnstile.render(el, {
      sitekey: el.dataset.sitekey,
      execution: 'execute',
      appearance: 'interaction-only',
      size: 'flexible',
      language: language(),
      'error-callback': () => false,
    });

    if (id === undefined) {
      widgets.delete(el);

      return;
    }

    widgets.set(el, id);
    window.turnstile.execute(id);
  });
};

const armWidgets = () => {
  document.querySelectorAll('.cf-turnstile').forEach((el) => {
    if (widgets.has(el) || el.dataset.armed) {
      return;
    }

    el.dataset.armed = 'true';
    const form = el.closest('form');

    if (form === null) {
      activate(el);

      return;
    }

    const start = () => activate(el);

    form.addEventListener('focusin', start, { once: true });
    form.addEventListener('input', start, { once: true });
  });
};

naja.addEventListener('complete', () => {
  armWidgets();
});

armWidgets();
